import type { ClampQcFinishResponse, ClampQcInventoryStatus, ClampQcReceiveDisposition } from "./clampQcFinishApi";
import { fetchClampQcInventoryStatus, finishClampQcWorkOrder } from "./clampQcFinishApi";

export type ClampQcReceiveRunOutcome =
  | { kind: "received"; result: ClampQcFinishResponse; statsBefore: ClampQcInventoryStatus }
  | { kind: "already_received"; stats: ClampQcInventoryStatus };

export type ClampQcReceiveRunDeps = {
  fetchInventoryStatus?: (slug: string) => Promise<ClampQcInventoryStatus>;
  finishWorkOrder?: (
    slug: string,
    disposition: ClampQcReceiveDisposition,
    close?: boolean,
  ) => Promise<ClampQcFinishResponse>;
};

const inflightReceiveBySlug = new Map<string, Promise<ClampQcReceiveRunOutcome>>();

/**
 * One receive POST per slug per page load, even when React Strict Mode remounts the receive page.
 */
export function runClampQcReceiveOnce(
  input: {
    slug: string;
    closeShort: boolean;
  },
  deps: ClampQcReceiveRunDeps = {},
): Promise<ClampQcReceiveRunOutcome> {
  const key = input.slug.trim().toLowerCase();
  if (key === "") {
    return Promise.reject(new Error("Work order slug is required."));
  }
  const existing = inflightReceiveBySlug.get(key);
  if (existing != null) return existing;

  const fetchInventoryStatus = deps.fetchInventoryStatus ?? fetchClampQcInventoryStatus;
  const finishWorkOrder = deps.finishWorkOrder ?? finishClampQcWorkOrder;

  const promise = (async (): Promise<ClampQcReceiveRunOutcome> => {
    const stats = await fetchInventoryStatus(input.slug);
    const receivable = stats.receivablePassedQuantity + stats.receivableFailedQuantity;
    if (receivable <= 0) {
      return { kind: "already_received", stats };
    }
    const close =
      input.closeShort || (stats.plannedQuantity > 0 && stats.notInspectedQuantity === 0);
    const disposition = input.closeShort ? "close_short" : "all";
    const result = await finishWorkOrder(input.slug, disposition, close);
    return { kind: "received", result, statsBefore: stats };
  })().finally(() => {
    inflightReceiveBySlug.delete(key);
  });

  inflightReceiveBySlug.set(key, promise);
  return promise;
}

/** Test-only: clear in-flight dedupe between cases. */
export function resetClampQcReceiveRunDedupeForTests(): void {
  inflightReceiveBySlug.clear();
}
